import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Settings2, Trash2 } from 'lucide-react';
import { useFieldArray, type UseFormReturn } from 'react-hook-form';

import { Select } from '@/components/ui/Select';
import { capitalizeWords } from '@/lib/format';
import {
  MAX_CURRICULUM_ITEMS,
  type ClassFormValues,
  type CurriculumFieldName,
} from '../schemas/class.schema';

export interface CurriculumListFieldProps {
  form: UseFormReturn<ClassFormValues>;
  name: CurriculumFieldName;
  /** Heading, and the stem of every row's screen-reader label. */
  label: string;
  /** What to say when the list is still empty. */
  emptyText: string;
  icon: ReactNode;
  /**
   * The school's subjects in this list's category — core subjects for the core
   * list, and so on. Only these can be picked, so a subject is never listed
   * under a heading it does not belong to.
   */
  subjects?: string[];
  /**
   * Opens the school's list for this category, to add, rename or delete a
   * subject without leaving the class. Absent, the list points to the Subjects
   * page instead.
   */
  onManage?: () => void;
  /** What Manage says to a screen reader; the school's list when not given. */
  manageLabel?: string;
}

/**
 * One list of a class's subjects — core subjects, electives or languages.
 *
 * Picked from the school's subject list, never typed. A typed box is how one
 * class came to teach "Maths" and another "Mathematics" as two subjects; a
 * subject is added or renamed on the school's list — on the Subjects page, or
 * through `onManage` here — and this list only says which of them the class
 * teaches.
 *
 * The three lists behave identically, so they are one component with a
 * different heading rather than three near-copies.
 */
export function CurriculumListField({
  form,
  name,
  label,
  emptyText,
  icon,
  subjects = [],
  onManage,
  manageLabel,
}: CurriculumListFieldProps) {
  const {
    control,
    watch,
    formState: { errors },
  } = form;
  const { fields, append, remove } = useFieldArray({ control, name });

  const entries = watch(name) ?? [];
  const listError = errors[name]?.root?.message ?? errors[name]?.message;

  const chosen = new Set(entries.map((entry) => entry.name.trim().toLowerCase()));
  const pickable = subjects
    .filter((subject) => !chosen.has(subject.toLowerCase()))
    .map((subject) => ({ value: subject, label: capitalizeWords(subject) }));
  const isFull = fields.length >= MAX_CURRICULUM_ITEMS;

  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-900/40 p-4 transition-all hover:border-slate-300 dark:hover:border-slate-700">
      <div className="flex items-center justify-between gap-2 pb-2.5 border-b border-slate-200/70 dark:border-slate-800">
        <div className="flex items-center gap-2">
          <span
            className="grid h-7 w-7 place-items-center rounded-lg bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 shrink-0"
            aria-hidden
          >
            {icon}
          </span>
          <span className="text-[13px] font-extrabold text-slate-700 dark:text-slate-100 tracking-tight">
            {label}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          {onManage && (
            <button
              type="button"
              onClick={onManage}
              className="inline-flex items-center gap-1 rounded-lg px-1.5 py-0.5 text-[11px] font-bold text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
              aria-label={manageLabel ?? `Manage your school's ${label.toLowerCase()}`}
            >
              <Settings2 className="h-3.5 w-3.5" aria-hidden />
              Manage
            </button>
          )}
          <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full text-[11px] font-bold bg-slate-200/70 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-mono">
            {fields.length}
          </span>
        </div>
      </div>

      {pickable.length > 0 && !isFull ? (
        <Select
          label={`Add to ${label.toLowerCase()}`}
          hideLabel
          placeholder={`Add from your ${label.toLowerCase()}`}
          options={pickable}
          // Always reads as the placeholder: choosing adds the subject to the list
          // below, and the picker is ready for the next one.
          value=""
          onChange={(event) => {
            if (!event.target.value) return;
            append({ name: event.target.value });
            form.clearErrors(name);
          }}
        />
      ) : (
        <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
          {isFull
            ? `Up to ${MAX_CURRICULUM_ITEMS} entries.`
            : subjects.length === 0
              ? `Your school has no ${label.toLowerCase()} yet. `
              : `Every one of your ${label.toLowerCase()} is on this list. `}
          {!isFull &&
            (onManage ? (
              <button
                type="button"
                onClick={onManage}
                className="font-bold text-indigo-600 dark:text-indigo-400 hover:underline cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 rounded"
              >
                Add one
              </button>
            ) : (
              <Link
                to="/admin/subjects"
                className="font-bold text-indigo-600 dark:text-indigo-400 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 rounded"
              >
                Add one on the Subjects page
              </Link>
            ))}
        </p>
      )}

      {listError && (
        <p role="alert" className="text-xs text-rose-500 font-semibold">
          {listError}
        </p>
      )}

      {fields.length === 0 ? (
        <p className="text-xs text-slate-400 dark:text-slate-500 font-medium leading-relaxed italic py-1">
          {emptyText}
        </p>
      ) : (
        <ul className="flex flex-col gap-2 pt-1">
          {fields.map((field, index) => {
            const entryName = entries[index]?.name || `entry ${index + 1}`;

            return (
              <li
                key={field.id}
                className="flex items-center gap-2 rounded-xl bg-white dark:bg-[#111c2e] border border-slate-200 dark:border-slate-700/80 px-2.5 py-1.5 shadow-2xs transition-all hover:border-slate-300 dark:hover:border-slate-600"
              >
                <span className="grid h-5 w-5 place-items-center rounded-md bg-slate-100 dark:bg-slate-800 text-[10.5px] font-black text-slate-500 dark:text-slate-400 shrink-0 font-mono">
                  {index + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs sm:text-[13px] font-bold text-slate-800 dark:text-slate-100 capitalize">
                    {entryName}
                  </p>
                  {errors[name]?.[index]?.name?.message && (
                    <p className="text-[11px] font-semibold text-rose-500 mt-0.5">
                      {errors[name]?.[index]?.name?.message}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  className="p-1 text-slate-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/40 rounded-lg transition-colors cursor-pointer shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-500"
                  aria-label={`Remove ${capitalizeWords(entryName)}`}
                  onClick={() => {
                    remove(index);
                    form.clearErrors(name);
                  }}
                >
                  <Trash2 className="h-3.5 w-3.5" aria-hidden />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
